const isValidCoordinate = (value: string | number, regex: RegExp, min: number, max: number): boolean => {
    const valueString = String(value).trim();

    // count the total number of decimal points — reject multiple dots (e.g. "1.2.3")
    const decimalCount = (valueString.match(/\./g) || []).length;
    if (decimalCount > 1) return false;

    // Check if the value matches the format for a valid decimal number
    const isValidFormat = regex.test(valueString);

    if (!isValidFormat) return false;

    // Convert to number and check the range explicitly
    const numberValue = parseFloat(valueString);
    return numberValue >= min && numberValue <= max;
};

export const isValidLatitude = (lat: string | number): boolean => {
    const latitudeRegex = /^-?(?:90(?:\.0+)?|[0-8]?\d(?:\.\d+)?)$/;
    return isValidCoordinate(lat, latitudeRegex, -90, 90);
};

export const isValidLongitude = (lon: string | number): boolean => {
    const longitudeRegex = /^-?(?:180(?:\.0+)?|(?:1[0-7]\d|\d{1,2})(?:\.\d+)?)$/;
    return isValidCoordinate(lon, longitudeRegex, -180, 180);
};

export const swapCoordinateLatLng = ([lng, lat]: [number, number]): [number, number] => [lat, lng];

/**
 * Validates if coordinates are valid (not NaN, finite, and not zero)
 * @param coordinates - Array of [latitude, longitude] coordinates
 * @returns boolean - True if coordinates are valid
 */
export const isValidCoordinates = (coordinates: [number, number]): boolean => {
    const [lat, lng] = coordinates;
    return lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);
};

/**
 * Transforms polygon coordinates from [longitude, latitude] to [latitude, longitude] format
 * @param polygonCoordinates - Array of coordinate rings in [longitude, latitude] format
 * @returns Array of coordinate rings in [latitude, longitude] format
 */
export const transformPolygonCoordinates = (polygonCoordinates: [number, number][][]): [number, number][][] => {
    return polygonCoordinates.map(ring => ring.map(coord => swapCoordinateLatLng(coord)));
};

/**
 * Filters an array of sites to only include those with valid coordinates
 * @param sites - Array of sites with coordinates property
 * @returns Array of sites with valid coordinates
 */
export const filterValidSites = <T extends { coordinates: [number, number] }>(sites: T[]): T[] => {
    return sites.filter(site => isValidCoordinates(site.coordinates));
};
